import { z } from "zod";
import type { Operation } from "../types";

// Live agent run trace (docs/agents/RUN-TRACE-PRD.md). Two operations:
//
//  • `runTrace.stream` — a streaming POST whose NDJSON body tails a run's
//    excellent_run_events as the worker subprocess emits them, then closes with
//    a final `status` + `done`. Served via `createStreamRoute`, read via
//    `useStream`. The run id rides in the BODY (not a path param) because
//    `useStream` builds the URL with no params — so the path stays static.
//
//  • `runTrace.control` — request pause | resume | abort on a live run. The
//    shell writes the request; the out-of-process runner applies the signal.
//
// This reuses the ONE sanctioned streaming seam; it does not weaken the
// no-`fetch` / `createRoute` rails for anything else.

/** One trace event as it crosses the wire (mirror of RunEventRow, sans the
 *  redundant worker_run_id). */
export const runEventWireSchema = z.object({
  id: z.number(),
  seq: z.number(),
  kind: z.string(),
  tool_use_id: z.string().nullable(),
  tool_name: z.string().nullable(),
  body: z.string().nullable(),
  meta: z.string().nullable(),
  created_at: z.string(),
});
export type RunEventWire = z.infer<typeof runEventWireSchema>;

export type RunTraceFrame =
  | { type: "event"; event: RunEventWire }
  | { type: "status"; status: string; control_state: string | null }
  | { type: "done" }
  | { type: "error"; message: string };

const runTraceFrameSchema = z.union([
  z.object({ type: z.literal("event"), event: runEventWireSchema }),
  z.object({
    type: z.literal("status"),
    status: z.string(),
    control_state: z.string().nullable(),
  }),
  z.object({ type: z.literal("done") }),
  z.object({ type: z.literal("error"), message: z.string() }),
]);

const streamBody = z.object({
  runId: z.number().int().positive(),
  /** Resume the tail after this seq (the client passes the last seq it has,
   *  so a reconnect doesn't replay the whole trace). Default 0 = from start. */
  since: z.number().int().min(0).optional(),
});

// coverage-ignore: stream needs mountStream peer — deferred (projects.ts NOTE); control is mounted
const streamOp: Operation<
  void,
  void,
  z.infer<typeof streamBody>,
  RunTraceFrame
> = {
  method: "POST",
  path: () => "/api/agents/run-stream",
  body: streamBody,
  // Frames are parsed client-side by `useStream`, not validated through this
  // schema (the NDJSON stream isn't a single envelope), so the cast mirrors
  // chat-stream.ts rather than contorting the union types.
  response: runTraceFrameSchema as unknown as z.ZodType<RunTraceFrame>,
};

const controlBody = z.object({
  runId: z.number().int().positive(),
  action: z.enum(["pause", "resume", "abort"]),
  note: z.string().max(2000).optional(),
});

export type RunControlResult = { ok: boolean; status: string | null };

const controlOp: Operation<
  void,
  void,
  z.infer<typeof controlBody>,
  RunControlResult
> = {
  method: "POST",
  path: () => "/api/agents/run-control",
  body: controlBody,
  response: z.object({ ok: z.boolean(), status: z.string().nullable() }),
};

export const runTrace = {
  stream: streamOp,
  control: controlOp,
};
